import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';

const services = [
  {
    icon: 'ri-play-circle-line',
    title: '全集在线观看',
    desc: '第一季 12 集剧集索引与观看入口，焦点大图直达最新剧集，追剧不迷路。',
  },
  {
    icon: 'ri-book-2-line',
    title: '剧情与角色资料',
    desc: '每集配备详尽剧情介绍与角色说明，帮助你更好地理解故事脉络。',
  },
  {
    icon: 'ri-search-2-line',
    title: '快捷搜索与筛选',
    desc: '支持关键词搜索、集数与更新时间排序，并可按更新状态筛选剧集。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '本地收藏追剧',
    desc: '一键收藏喜欢的剧集，收藏数据保存在浏览器本地，刷新页面依然保留。',
  },
];

const usageSteps = [
  '通过顶部导航进入「动画首页」「剧集分类」「搜索」「关于」等页面。',
  '在分类页使用筛选与排序，快速找到想看的集数并点击进入详情页。',
  '在详情页可观看演示播放器、阅读详细剧情，并使用上下集导航连续追剧。',
  '点击「收藏本集」保存喜爱的剧集，收藏状态会保存在你的浏览器本地。',
];

const socials = [
  { icon: 'ri-mail-line', label: 'contact@wuyanzhiyue.demo', href: 'mailto:contact@wuyanzhiyue.demo' },
  { icon: 'ri-weibo-line', label: '微博 @无颜之月追番', href: '#' },
  { icon: 'ri-bilibili-line', label: '哔哩哔哩 · 无颜之月', href: '#' },
];

export default function About() {
  usePageMeta({
    title: '关于本站 - 无颜之月动漫第一季全集',
    description:
      '了解无颜之月动漫第一季全集平台的定位与服务：全集剧集索引、剧情与角色资料、搜索筛选与本地收藏功能，以及联系方式与版权声明。',
    keywords: '无颜之月关于,动漫视频平台,无颜之月动漫,剧情资料,版权声明',
  });

  return (
    <div className="w-full px-4 md:px-8 pt-24 md:pt-28">
      <nav className="flex items-center flex-wrap gap-1 text-xs text-foreground-500 mb-6">
        <Link to="/" className="hover:text-accent-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-300">关于</span>
      </nav>

      <section className="rounded-xl bg-gradient-to-br from-primary-900/50 via-background-900 to-secondary-900/40 border border-primary-800/40 p-6 md:p-10">
        <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-accent-300 uppercase">
          <i className="ri-moon-clear-line"></i>
          About us
        </span>
        <h1 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-foreground-50 leading-tight">
          关于「无颜之月动漫第一季全集」
        </h1>
        <p className="mt-5 text-sm md:text-base text-foreground-300 leading-relaxed max-w-3xl">
          本站是一个专注「无颜之月」动画第一季全集的演示型动漫视频平台。我们围绕剧集播放、剧情介绍、角色资料与相关推荐展开，
          希望为喜爱这部作品的动漫迷提供权威、完整、易用的查阅与观看体验。整站采用电影海报风格的夜色界面，
          让每一次追剧都像走进一段神秘的月夜故事。
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <Link
            to="/category"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-950 hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-play-list-2-line"></i>
            查看全部剧集
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-background-800/70 border border-primary-500/40 px-6 py-3 text-sm font-medium text-foreground-100 hover:bg-background-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-6">我们的服务</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((service) => (
            <div
              key={service.title}
              className="rounded-lg bg-background-900 border border-primary-900/40 p-5 hover:border-primary-500/50 transition-colors"
            >
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-300 mb-3">
                <i className={`${service.icon} text-xl`}></i>
              </span>
              <h3 className="font-heading text-base font-semibold text-foreground-50">{service.title}</h3>
              <p className="mt-1.5 text-xs text-foreground-400 leading-relaxed">{service.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        <section className="rounded-lg bg-background-900 border border-primary-900/40 p-6 md:p-8">
          <h2 className="font-heading font-bold text-xl text-foreground-50 mb-5">使用说明</h2>
          <ol className="space-y-4">
            {usageSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-500/20 border border-secondary-500/40 text-secondary-200 text-xs font-semibold shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-foreground-300 leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="rounded-lg bg-background-900 border border-primary-900/40 p-6 md:p-8">
          <h2 className="font-heading font-bold text-xl text-foreground-50 mb-5">联系我们</h2>
          <p className="text-sm text-foreground-400 leading-relaxed mb-5">
            如果你有任何建议、反馈或合作意向，欢迎通过以下方式与我们取得联系。
          </p>
          <div className="space-y-3">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.href}
                className="flex items-center gap-3 rounded-md bg-background-800/60 border border-primary-900/40 px-4 py-3 hover:border-primary-500/60 transition-colors cursor-pointer"
              >
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
                  <i className={`${social.icon} text-lg`}></i>
                </span>
                <span className="text-sm text-foreground-200">{social.label}</span>
                <i className="ri-arrow-right-up-line ml-auto text-foreground-500"></i>
              </a>
            ))}
          </div>
        </section>
      </div>

      <section className="mt-12 rounded-lg bg-background-900 border border-primary-900/40 p-6 md:p-8">
        <h2 className="font-heading font-bold text-xl text-foreground-50 mb-5">版权声明</h2>
        <div className="space-y-4 text-sm text-foreground-400 leading-relaxed">
          <p>
            本站为演示型站点，所有剧集名称、剧情介绍与角色信息均为虚构示例，仅用于展示网站功能与界面效果，
            不包含任何未授权的真实内容、真实演员信息或受版权保护的影像资料。
          </p>
          <p>
            站内的视频播放区域为前端模拟演示效果，不代表真实的流媒体播放能力，也不会实际播放任何受版权保护的内容。
            如需观看正版内容，请前往官方授权渠道。
          </p>
          <p>本站的界面设计、文案与代码版权归本站所有，仅供学习与演示使用，未经许可请勿用于商业用途。</p>
        </div>
      </section>
    </div>
  );
}